import { AxiosError } from 'axios'
import { useMutation, useQueryClient } from 'react-query'
import { PackageInfo, Sticker } from '../../types'
import { toFormData } from '../../utils/uploadPackageInfoUtils'
import { PACKAGE_INFO_KEY } from './usePackage'
import { deleteStickerAPI, replaceStickerAPI } from '../../api/sticker'
import { processAnExpiredSession } from '../../utils/errorHandling'
import * as Sentry from '@sentry/react'

type ReplaceStickerFormData = {
  userId: string
  stickerId: string
  sessionToken: string
  tobeFile: File
  packageId: string
}

export const useUpdateSticker = (packageId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      userId,
      packageId,
      stickerId,
      sessionToken,
      tobeFile,
    }: ReplaceStickerFormData) => {
      const formData = toFormData<ReplaceStickerFormData>({
        userId,
        packageId,
        stickerId,
        sessionToken,
        tobeFile,
      })
      return replaceStickerAPI(formData)
    },
    {
      onSuccess: async (data) => {
        const stickerOid = data.value.objectId
        const changedStickerOrigin = data.value.origin.url
        const prePackageInfo = queryClient.getQueryData<PackageInfo>([
          PACKAGE_INFO_KEY,
          packageId,
        ])
        if (prePackageInfo && prePackageInfo.emoticons) {
          const newEmoticons = prePackageInfo.emoticons.map((emoticon) => {
            if (emoticon.objectId === stickerOid) {
              if (emoticon.stickerInspectionData) {
                return {
                  ...emoticon,
                  origin: changedStickerOrigin,
                  textLang: data.textLang,
                  emojiCategories: data.emojiCategories,
                  stickerInspectionData: {
                    ...emoticon.stickerInspectionData,
                    needTagEditing: false,
                    needImgEditing: false,
                    needStickerDeleting: false,
                  },
                }
              } else {
                return {
                  ...data.value,
                  origin: changedStickerOrigin,
                  textLang: data.textLang,
                  emojiCategories: data.emojiCategories,
                }
              }
            }
            return emoticon
          })
          const newPackageInfo: PackageInfo = {
            ...prePackageInfo,
            emoticons: newEmoticons,
          }
          queryClient.setQueryData(
            [PACKAGE_INFO_KEY, packageId],
            newPackageInfo,
          )
        }
      },
      onError: (e: AxiosError) => {
        console.dir(e)
        switch (e.response?.status) {
          case 403:
            processAnExpiredSession()
            break
          default:
            Sentry.captureException(
              new Error(
                `update sticker pkgid:${packageId} message ::` + e.message,
              ),
            )
        }
      },
      mutationKey: packageId,
    },
  )
}

export const useDeleteSticker = (packageId: string) => {
  const queryClient = useQueryClient()

  return useMutation(deleteStickerAPI, {
    mutationKey: packageId,
    onSuccess: async (_, { stickerOid }) => {
      const prePackageInfo = queryClient.getQueryData<PackageInfo>([
        PACKAGE_INFO_KEY,
        packageId,
      ])
      if (prePackageInfo && prePackageInfo.emoticons) {
        const newEmoticons = prePackageInfo.emoticons.filter(
          (emoticon) => emoticon.objectId !== stickerOid,
        )
        const newPackageInfo: PackageInfo = {
          ...prePackageInfo,
          emoticons: newEmoticons,
        }
        queryClient.setQueryData([PACKAGE_INFO_KEY, packageId], newPackageInfo)
      }
    },
    onError: (e: AxiosError) => {
      console.dir(e)
      switch (e.response?.status) {
        case 403:
          processAnExpiredSession()
          break
        default:
          Sentry.captureException(
            new Error(
              `delete sticker pkgid:${packageId} message ::` + e.message,
            ),
          )
      }
    },
  })
}
